/**
 * 파트너스테이션 계정 생성·수정 모달 — RDS Dialog.
 * 생성은 이메일·담당자명·초기 비밀번호 필수, 수정은 바뀐 항목만 PATCH로 보낸다.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type {
  PartnerCompanyAccount,
  PartnerCompanyAccountUpdateRequest,
} from '@/entities/partner-company/api/partner-company.api';
import { FormTextField } from '@/shared/components/FormTextField';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { PasswordInput } from '@/shared/components/ui/password-input';
import {
  popupPrimaryButtonClassName,
  popupSecondaryButtonClassName,
} from '@/shared/components/ui/popup-styles';
import { Switch } from '@/shared/components/ui/switch';

/** 서버 제약: 비밀번호 10~128자 */
const PASSWORD_MIN = 10;
const PASSWORD_MAX = 128;

const baseFields = {
  loginEmail: z
    .string()
    .trim()
    .min(1, '로그인 이메일을 입력해주세요')
    .max(255, '이메일은 255자 이하로 입력해주세요')
    .email('올바른 이메일 형식이 아닙니다'),
  name: z
    .string()
    .trim()
    .min(1, '담당자명을 입력해주세요')
    .max(100, '담당자명은 100자 이하로 입력해주세요'),
  active: z.boolean(),
};

const passwordRule = z
  .string()
  .min(PASSWORD_MIN, `비밀번호는 ${PASSWORD_MIN}자 이상 입력해주세요`)
  .max(PASSWORD_MAX, `비밀번호는 ${PASSWORD_MAX}자 이하로 입력해주세요`);

export const partnerAccountCreateSchema = z.object({
  ...baseFields,
  password: passwordRule,
});

/** 수정 시 비밀번호는 바꿀 때만 입력한다 (빈 값이면 유지) */
export const partnerAccountUpdateSchema = z.object({
  ...baseFields,
  password: z.union([z.literal(''), passwordRule]),
});

export type PartnerAccountFormValues = z.infer<
  typeof partnerAccountCreateSchema
>;

/**
 * 수정 요청 본문 — 바뀐 항목만 담는다.
 * 서버는 이메일·비밀번호·사용 여부가 바뀌면 인증 버전을 올려 기존 로그인을 끊으므로
 * 값이 같은 항목을 보내지 않는다.
 */
export const buildPartnerAccountUpdateRequest = (
  account: PartnerCompanyAccount,
  values: PartnerAccountFormValues,
): PartnerCompanyAccountUpdateRequest => {
  const request: PartnerCompanyAccountUpdateRequest = {};
  const loginEmail = values.loginEmail.trim();
  const name = values.name.trim();
  if (loginEmail !== account.loginEmail) request.loginEmail = loginEmail;
  if (name !== account.name) request.name = name;
  if (values.password) request.password = values.password;
  if (values.active !== account.active) request.active = values.active;
  return request;
};

const EMPTY_VALUES: PartnerAccountFormValues = {
  loginEmail: '',
  name: '',
  password: '',
  active: true,
};

interface PartnerAccountFormModalProps {
  open: boolean;
  companyName: string;
  /** 없으면 생성 모드 */
  account: PartnerCompanyAccount | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: PartnerAccountFormValues) => void;
}

export const PartnerAccountFormModal: React.FC<
  PartnerAccountFormModalProps
> = ({ open, companyName, account, submitting, onClose, onSubmit }) => {
  const isEdit = account != null;
  const form = useForm<PartnerAccountFormValues>({
    resolver: zodResolver(
      isEdit ? partnerAccountUpdateSchema : partnerAccountCreateSchema,
    ),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      account
        ? {
            loginEmail: account.loginEmail,
            name: account.name,
            password: '',
            active: account.active,
          }
        : EMPTY_VALUES,
    );
  }, [open, account, form]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* 입력 중 실수로 닫히지 않게 바깥 클릭·ESC는 막고, X·취소로만 닫는다 */}
      <DialogContent
        className="max-w-[480px]"
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader className="items-start text-left">
          <DialogTitle>{isEdit ? '계정 수정' : '계정 생성'}</DialogTitle>
          <DialogDescription className="text-left">
            {companyName} 파트너스테이션 로그인 계정
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            id="partner-account-form"
            className="space-y-4"
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
          >
            <FormTextField
              control={form.control}
              name="loginEmail"
              label="로그인 이메일"
              type="email"
              placeholder="manager@partner.co.kr"
              required
            />
            <FormTextField
              control={form.control}
              name="name"
              label="담당자명"
              placeholder="홍길동"
              required
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required={!isEdit}>
                    {isEdit ? '새 비밀번호' : '초기 비밀번호'}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      autoComplete="new-password"
                      placeholder={
                        isEdit
                          ? '변경할 때만 입력 (10자 이상)'
                          : '10자 이상 입력'
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isEdit ? (
              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>사용 여부</FormLabel>
                    <div className="flex h-10 items-center">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          aria-label="계정 사용 여부"
                        />
                      </FormControl>
                    </div>
                  </FormItem>
                )}
              />
            ) : null}
            {isEdit ? (
              <p className="text-sm text-ruda-text-tertiary">
                이메일·비밀번호·사용 여부를 바꾸면 이 계정의 기존 로그인이
                해제됩니다.
              </p>
            ) : null}
          </form>
        </Form>

        <DialogFooter>
          <Button
            type="button"
            className={popupSecondaryButtonClassName}
            onClick={onClose}
            disabled={submitting}
          >
            취소
          </Button>
          <Button
            type="submit"
            form="partner-account-form"
            className={popupPrimaryButtonClassName}
            disabled={submitting}
          >
            {submitting ? '저장 중…' : isEdit ? '저장' : '계정 생성'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
